import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { formatDateToDDMMYYYY } from '../../utils';
import InvoicePreflightDialog, { PreflightReport } from './InvoicePreflightDialog';

const stickyCellStyle = {
  background: '#262626',
  fontWeight: 'bold',
  minWidth: 50,
  color: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

interface PreviewInvoice {
  id: number;
  deal_id: number;
  reg_plate: string;
  selling_price: string;
  pipeline_letter: string;
  vat_rate: string;
  created_at: string;
  pipeline_source: string;
  is_late?: boolean;
}

interface PreviewData {
  client_id: string;
  invoicing_type: string;
  agency_name: string;
  agency_id: number;
  cg_fee: number;
  da_fee: number;
  invoices: PreviewInvoice[];
  /** Factures complémentaires CG acceptées, portées sur ce relevé (spécification CG agence v1.3, §10.10). */
  complements?: Array<{
    id: number;
    deal_id: number;
    reg_plate: string | null;
    motif: string;
    amount_ht: number;
    amount_ttc: number;
    items: Array<{ title: string; quantity: string; unit_price: { value: string } }>;
  }>;
}

interface GeneratingState {
  [key: string]: boolean; // key is client_id
}

interface GeneratedInvoice {
  invoice_number: string;
  invoice_firebase_url: string;
  total_amount: number;
  generation_date: string;
}

interface GeneratedInvoicesState {
  [key: string]: GeneratedInvoice | null; // key is `${client_id}_${agency_id}`
}

const CGPendingInvoicesPreview = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatingClients, setGeneratingClients] = useState<GeneratingState>({});
  const [generatingClientsNoEmail, setGeneratingClientsNoEmail] = useState<GeneratingState>({});
  const [generatedInvoices, setGeneratedInvoices] = useState<GeneratedInvoicesState>({});
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Contrôles de cohérence interposés entre le clic et l'appel à Qonto.
  // Cf. InvoicePreflightDialog : une facture émise ne se rattrape pas.
  const [preflightOpen, setPreflightOpen] = useState<boolean>(false);
  const [preflightReport, setPreflightReport] = useState<PreflightReport | null>(null);
  const [preflightLoading, setPreflightLoading] = useState<boolean>(false);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{ label: string; run: () => void } | null>(null);

  /**
   * Ouvre la modale, lance la vérification, et ne déclenche la génération
   * qu'après validation explicite. La modale s'affiche immédiatement en état de
   * chargement : l'utilisateur voit qu'un contrôle a lieu, plutôt qu'un bouton
   * qui semble ne rien faire.
   */
  const requestGeneration = async (options: { clientId?: string; label: string; run: () => void }) => {
    setPendingAction({ label: options.label, run: options.run });
    setPreflightReport(null);
    setPreflightError(null);
    setPreflightLoading(true);
    setPreflightOpen(true);

    try {
      const url = options.clientId
        ? `/api/qonto/preflight-pending-invoices?clientId=${encodeURIComponent(options.clientId)}`
        : '/api/qonto/preflight-pending-invoices';
      const response = await makeAuthenticatedApiCall('get', url);
      const payload = response.data as { success?: boolean; data?: PreflightReport; error?: string };

      if (payload?.success && payload.data) {
        setPreflightReport(payload.data);
      } else {
        setPreflightError(payload?.error || "La vérification n'a pas abouti.");
      }
    } catch (error) {
      console.error('Error running invoice preflight:', error);
      // Échec de la vérification = refus d'émettre. Ne pas pouvoir contrôler
      // n'est pas équivalent à un contrôle vert.
      setPreflightError(
        "La vérification de cohérence n'a pas pu aboutir. Par précaution, la génération est bloquée. Réessayez, et si le problème persiste prévenez Luc.",
      );
    }

    setPreflightLoading(false);
  };

  const handlePreflightConfirm = () => {
    setPreflightOpen(false);
    const action = pendingAction;
    setPendingAction(null);
    action?.run();
  };

  const handlePreflightCancel = () => {
    setPreflightOpen(false);
    setPendingAction(null);
  };

  const fetchPreviewData = async () => {
    setIsLoading(true);
    try {
      const response = await makeAuthenticatedApiCall('get', '/api/qonto/preview-pending-invoices');
      setPreviewData((response.data as PreviewData[]) || []);
    } catch (error) {
      console.error('Error fetching preview data:', error);
    }
    setIsLoading(false);
  };

  // Calculate billing period based on invoicing type
  const calculateBillingPeriod = (invoicingType: string): string => {
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    const monthNames = [
      'Janvier',
      'Février',
      'Mars',
      'Avril',
      'Mai',
      'Juin',
      'Juillet',
      'Août',
      'Septembre',
      'Octobre',
      'Novembre',
      'Décembre',
    ];

    if (invoicingType === 'Monthly') {
      const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      const lastYear = currentMonth === 1 ? currentYear - 1 : currentYear;
      return `${monthNames[lastMonth - 1]} ${lastYear}`;
    } else if (invoicingType === 'Bi-weekly') {
      if (currentDay >= 15) {
        return `1 - 14 ${monthNames[currentMonth - 1]} ${currentYear}`;
      } else {
        const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const lastYear = currentMonth === 1 ? currentYear - 1 : currentYear;
        const lastDayOfMonth = new Date(lastYear, lastMonth, 0).getDate();
        return `15 - ${lastDayOfMonth} ${monthNames[lastMonth - 1]} ${lastYear}`;
      }
    }
    return '';
  };

  // Check for existing generated invoices
  const checkGeneratedInvoices = async (data: PreviewData[]) => {
    const invoicesStatus: GeneratedInvoicesState = {};

    for (const item of data) {
      const key = `${item.client_id}_${item.agency_id}`;
      const billingPeriod = calculateBillingPeriod(item.invoicing_type);

      try {
        const response = await makeAuthenticatedApiCall(
          'get',
          `/api/qonto/generated-invoice/${item.client_id}/${item.agency_id}?billingPeriod=${encodeURIComponent(billingPeriod)}`,
        );

        if (response.status === 200) {
          const responseData = response.data as { success: boolean; data: GeneratedInvoice };
          if (responseData.success) {
            invoicesStatus[key] = responseData.data;
          } else {
            invoicesStatus[key] = null;
          }
        } else {
          invoicesStatus[key] = null;
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          invoicesStatus[key] = null;
        } else {
          console.error(`Error checking invoice for ${key}:`, error);
          invoicesStatus[key] = null;
        }
      }
    }

    setGeneratedInvoices(invoicesStatus);
  };

  useEffect(() => {
    fetchPreviewData();
  }, []);

  useEffect(() => {
    if (previewData.length > 0) {
      checkGeneratedInvoices(previewData);
    }
  }, [previewData]);

  // Group preview data by agency
  const groupedByAgency = useMemo(() => {
    const groups = new Map<number, { agency_name: string; clients: PreviewData[] }>();

    previewData.forEach((item) => {
      const agencyId = item.agency_id;
      if (!groups.has(agencyId)) {
        groups.set(agencyId, {
          agency_name: item.agency_name,
          clients: [],
        });
      }
      groups.get(agencyId)!.clients.push(item);
    });

    return Array.from(groups.entries()).map(([agencyId, data]) => ({
      agencyId,
      ...data,
    }));
  }, [previewData]);

  // Define alternating colors from theme
  const agencyColors = [
    'rgba(253, 209, 95, 0.3)', // sunburst
    'rgba(253, 199, 95, 0.3)', // amber
    'rgba(253, 187, 95, 0.3)', // saffron
    'rgba(239, 143, 53, 0.3)', // orange
  ];

  const handleGenerateInvoices = async () => {
    setIsGenerating(true);
    try {
      const response = await makeAuthenticatedApiCall('get', '/api/qonto/generate-pending-invoices');

      if (response.status === 200) {
        const data = response.data as { message?: string };
        setSnackbarMessage(data.message || "Factures générées avec succès. Voir l'historique des générations");
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        // Navigate back to the list after successful generation
        setTimeout(() => {
          navigate('/pending-invoices');
        }, 2000);
      } else if (response.status === 204) {
        const data = response.data as { message?: string };
        setSnackbarMessage(data.message || 'Aucune facture en attente de traitement.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Erreur lors de la génération des factures');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error generating invoices:', error);

      if (error.response) {
        const errorData = error.response.data;
        setSnackbarMessage(errorData.message || 'Erreur lors de la génération des factures');
      } else {
        setSnackbarMessage('Erreur de connexion lors de la génération des factures');
      }
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setIsGenerating(false);
  };

  const handleGenerateSingleInvoice = async (clientId: string, agencyName: string) => {
    setGeneratingClients((prev) => ({ ...prev, [clientId]: true }));
    try {
      const response = await makeAuthenticatedApiCall('post', '/api/qonto/generate-single-invoice', {
        clientId,
      });

      if (response.status === 200) {
        const data = response.data as { message?: string };
        setSnackbarMessage(
          data.message || `Facture pour ${agencyName} générée avec succès. Voir l'historique des générations`,
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        // Refresh preview data to remove the generated invoice
        await fetchPreviewData();
      } else if (response.status === 204) {
        const data = response.data as { message?: string };
        setSnackbarMessage(data.message || 'Aucune facture à générer pour ce client.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(`Erreur lors de la génération de la facture pour ${agencyName}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error generating single invoice:', error);

      if (error.response) {
        const errorData = error.response.data;
        setSnackbarMessage(errorData.message || `Erreur lors de la génération de la facture pour ${agencyName}`);
      } else {
        setSnackbarMessage(`Erreur de connexion lors de la génération de la facture pour ${agencyName}`);
      }
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setGeneratingClients((prev) => ({ ...prev, [clientId]: false }));
  };

  const handleGenerateSingleInvoiceNoEmail = async (
    clientId: string,
    agencyId: number,
    agencyName: string,
    invoicingType: string,
  ) => {
    setGeneratingClientsNoEmail((prev) => ({ ...prev, [clientId]: true }));
    try {
      const response = await makeAuthenticatedApiCall('post', '/api/qonto/generate-single-invoice-no-email', {
        clientId,
      });

      if (response.status === 200) {
        setSnackbarMessage(
          `Facture pour ${agencyName} générée avec succès (sans email). Voir l'historique des générations`,
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        // Check for the generated invoice
        const key = `${clientId}_${agencyId}`;
        const billingPeriod = calculateBillingPeriod(invoicingType);

        try {
          const invoiceResponse = await makeAuthenticatedApiCall(
            'get',
            `/api/qonto/generated-invoice/${clientId}/${agencyId}?billingPeriod=${encodeURIComponent(billingPeriod)}`,
          );

          if (invoiceResponse.status === 200) {
            const responseData = invoiceResponse.data as { success: boolean; data: GeneratedInvoice };
            if (responseData.success) {
              setGeneratedInvoices((prev) => ({
                ...prev,
                [key]: responseData.data,
              }));
            }
          }
        } catch (error) {
          console.error('Error fetching generated invoice:', error);
        }
      } else {
        setSnackbarMessage(`Erreur lors de la génération de la facture pour ${agencyName}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error generating invoice without email:', error);

      if (error.response) {
        const errorData = error.response.data;
        setSnackbarMessage(errorData.message || `Erreur lors de la génération de la facture pour ${agencyName}`);
      } else {
        setSnackbarMessage(`Erreur de connexion lors de la génération de la facture pour ${agencyName}`);
      }
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setGeneratingClientsNoEmail((prev) => ({ ...prev, [clientId]: false }));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (isLoading) {
    return (
      <Grid container direction="column" justifyContent="center" alignItems="center" marginTop={2}>
        <Typography sx={{ marginBottom: 2 }} variant="body1">
          Chargement
        </Typography>
        <CircularProgress size={70} />
      </Grid>
    );
  }

  if (previewData.length === 0) {
    return (
      <Container maxWidth={false}>
        <Typography variant="h4" gutterBottom sx={{ marginTop: 4 }}>
          Factures prêtes à générer
        </Typography>
        <Paper sx={{ marginTop: 5, padding: 4 }} elevation={3}>
          <Typography variant="body1" align="center">
            Aucune facture prête à générer pour le moment.
          </Typography>
          <Box display="flex" justifyContent="center" marginTop={3}>
            <Button
              variant="outlined"
              sx={{
                fontWeight: 600,
                color: 'black !important',
                borderColor: '#fdbb5f',
              }}
              onClick={() => navigate('/pending-invoices')}
            >
              Retour à la liste
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
            <Button
              variant="contained"
              sx={{
                marginBottom: 2,
                marginTop: 4,
                fontWeight: 700,
                height: 33,
                color: 'black !important',
              }}
              className="dealActionButton"
              onClick={() =>
                requestGeneration({
                  label: 'Générer toutes les factures',
                  run: handleGenerateInvoices,
                })
              }
              disabled={isGenerating}
            >
              {isGenerating ? <CircularProgress size={24} /> : 'Générer toutes les factures et envoyer les mails'}
            </Button>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/invoice-batches')}
              sx={{
                textDecoration: 'underline',
                color: '#262626',
                cursor: 'pointer',
                '&:hover': {
                  color: '#fdbb5f',
                },
              }}
            >
              Historique des générations de factures
            </Link>
          </Box>
        </Box>
      </Box>
      <Typography variant="h4" gutterBottom sx={{ marginTop: 4 }}>
        Factures prêtes à générer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Voici les factures de la période précédente, prêtes à être générées maintenant.
      </Typography>

      {groupedByAgency.map((agencyGroup, agencyIndex) => (
        <Paper
          key={agencyGroup.agencyId}
          sx={{
            mb: 4,
            p: 2,
            backgroundColor: agencyColors[agencyIndex % agencyColors.length],
            borderRadius: 2,
          }}
          elevation={3}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#262626' }}>
            {agencyGroup.agency_name}
          </Typography>

          {agencyGroup.clients.map((client) => {
            const cgInvoices = client.invoices.filter((inv) => inv.pipeline_source === 'f');
            const daInvoices = client.invoices.filter((inv) => inv.pipeline_source === 'da');
            const cgCount = cgInvoices.length;
            const daCount = daInvoices.length;

            // Calculate amounts
            const cgTotalHT = cgInvoices.reduce((sum, inv) => sum + parseFloat(inv.selling_price), 0);
            const cgFee = client.cg_fee || 10.0;
            const daFee = client.da_fee || 10.0;
            const daTotalHT = daCount * daFee; // Use dynamic da_fee instead of selling_price
            const commissionsHT = cgCount * cgFee;
            const commissionsWithVAT = commissionsHT * 1.2; // 20% VAT
            const daWithVAT = daTotalHT * 1.2; // 20% VAT
            const complementsTTC = (client.complements ?? []).reduce((sum, c) => sum + c.amount_ttc, 0);
            const totalTTC = cgTotalHT + daWithVAT + commissionsWithVAT + complementsTTC;

            const isClientGenerating = generatingClients[client.client_id] || false;
            const isClientGeneratingNoEmail = generatingClientsNoEmail[client.client_id] || false;
            const invoiceKey = `${client.client_id}_${client.agency_id}`;
            const generatedInvoice = generatedInvoices[invoiceKey];

            // Format the count display
            const complementCount = client.complements?.length ?? 0;
            const countDisplay =
              [cgCount > 0 ? `${cgCount} CG` : '', daCount > 0 ? `${daCount} DA` : '', complementCount > 0 ? `${complementCount} complément${complementCount > 1 ? 's' : ''}` : '']
                .filter(Boolean)
                .join(' et ') || '0 CG';

            return (
              <Box key={client.client_id} sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#464646' }}>
                      Client {client.client_id} - Type de facturation: {client.invoicing_type}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Total TTC : {totalTTC.toFixed(2)} €
                    </Typography>
                    {cgCount > 0 && (
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#666' }}>
                        • {cgCount} CG : {cgTotalHT.toFixed(2)} € HT + commissions ({cgCount} × {cgFee.toFixed(2)} €) :{' '}
                        {commissionsHT.toFixed(2)} € HT = {(cgTotalHT + commissionsWithVAT).toFixed(2)} € TTC
                      </Typography>
                    )}
                    {daCount > 0 && (
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#666' }}>
                        • {daCount} DA : {daCount} × {daFee.toFixed(2)} € = {daTotalHT.toFixed(2)} € HT + TVA (20%) ={' '}
                        {daWithVAT.toFixed(2)} € TTC
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                    <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
                      {generatedInvoice && (
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: 14,
                            backgroundColor: 'green',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'darkgreen',
                            },
                          }}
                          onClick={() => window.open(generatedInvoice.invoice_firebase_url, '_blank')}
                        >
                          Facture déjà générée le {formatDateToDDMMYYYY(generatedInvoice.generation_date)}
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          color: 'black !important',
                        }}
                        className="dealActionButton"
                        onClick={() =>
                          requestGeneration({
                            clientId: client.client_id,
                            label: 'Générer la facture',
                            run: () =>
                              handleGenerateSingleInvoiceNoEmail(
                                client.client_id,
                                client.agency_id,
                                client.agency_name,
                                client.invoicing_type,
                              ),
                          })
                        }
                        disabled={isClientGeneratingNoEmail || isClientGenerating || isGenerating}
                      >
                        {isClientGeneratingNoEmail ? (
                          <CircularProgress size={20} />
                        ) : (
                          'Générer et télécharger la facture'
                        )}
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          color: 'black !important',
                        }}
                        className="dealActionButton"
                        onClick={() =>
                          requestGeneration({
                            clientId: client.client_id,
                            label: 'Générer et envoyer',
                            run: () => handleGenerateSingleInvoice(client.client_id, client.agency_name),
                          })
                        }
                        disabled={isClientGeneratingNoEmail || isClientGenerating || isGenerating}
                      >
                        {isClientGenerating ? (
                          <CircularProgress size={20} />
                        ) : (
                          'Générer la facture et envoyer le mail'
                        )}
                      </Button>
                    </Box>
                    {generatedInvoice && (
                      <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', textAlign: 'right' }}>
                        Générer une nouvelle facture pour la même période (complémentaire à la précédente).
                      </Typography>
                    )}
                  </Box>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={stickyCellStyle}>Immatriculation</TableCell>
                        <TableCell sx={stickyCellStyle}>Deal ID</TableCell>
                        <TableCell sx={stickyCellStyle}>Prix</TableCell>
                        <TableCell sx={stickyCellStyle}>Type</TableCell>
                        <TableCell sx={stickyCellStyle}>TVA</TableCell>
                        <TableCell sx={stickyCellStyle}>Date de création</TableCell>
                        <TableCell sx={stickyCellStyle}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {client.invoices.map((invoice) => (
                        <TableRow
                          key={invoice.id}
                          sx={{
                            backgroundColor: invoice.is_late
                              ? 'rgba(255, 152, 0, 0.08)'
                              : 'rgba(33, 150, 243, 0.05)',
                          }}
                        >
                          <TableCell>{invoice.reg_plate}</TableCell>
                          <TableCell>{invoice.deal_id}</TableCell>
                          <TableCell>{parseFloat(invoice.selling_price).toFixed(2)} €</TableCell>
                          <TableCell>{invoice.pipeline_letter.toUpperCase()}</TableCell>
                          <TableCell>{invoice.vat_rate}</TableCell>
                          <TableCell>{formatDateToDDMMYYYY(invoice.created_at)}</TableCell>
                          <TableCell>
                            {invoice.is_late ? (
                              <Tooltip
                                title="Ce deal provient d'une période antérieure. Il n'a pas pu être facturé à temps car le récépissé (CTVO ou DA) n'était pas encore disponible."
                                arrow
                                placement="left"
                              >
                                <InfoOutlinedIcon
                                  sx={{
                                    color: '#ff9800',
                                    fontSize: 20,
                                    cursor: 'help',
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip
                                title="Ce deal appartient à la période de facturation en cours. Il sera inclus dans la facture générée."
                                arrow
                                placement="left"
                              >
                                <CheckCircleOutlineIcon
                                  sx={{
                                    color: '#2196f3',
                                    fontSize: 20,
                                    cursor: 'help',
                                  }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(client.complements ?? []).map((complement) => (
                        <TableRow key={`complement-${complement.id}`} sx={{ backgroundColor: 'rgba(109, 40, 217, 0.06)' }}>
                          <TableCell>{complement.reg_plate ?? ''}</TableCell>
                          <TableCell>{complement.deal_id}</TableCell>
                          <TableCell>{complement.amount_ttc.toFixed(2)} € TTC</TableCell>
                          <TableCell>Complément</TableCell>
                          <TableCell>{complement.items.map((item) => item.title).join(', ')}</TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
        </Paper>
      ))}

      <InvoicePreflightDialog
        open={preflightOpen}
        report={preflightReport}
        isLoading={preflightLoading}
        errorMessage={preflightError}
        actionLabel={pendingAction?.label ?? 'Générer'}
        onCancel={handlePreflightCancel}
        onConfirm={handlePreflightConfirm}
      />

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          action={
            snackbarSeverity === 'success' && snackbarMessage.includes('historique') ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  handleSnackbarClose();
                  navigate('/invoice-batches');
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Voir l'historique
              </Button>
            ) : null
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CGPendingInvoicesPreview;
